import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import AddNewHotelForm from "./AddNewHotelForm";
import Utils from "../../Utils";

const HotelAccessRequest = ({
  userData,
  open = true,
  onClose = () => {},
  onRequestSubmitted = () => {},
}) => {
  const pageSize = 5;
  const [brands, setBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showAddHotelForm, setShowAddHotelForm] = useState(false);
  const [filters, setFilters] = useState({
    brand_id: "",
    country_id: "",
    keyword: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [results, setResults] = useState({
    loading: false,
    searched: false,
    data: [],
  });
  const MySwal = withReactContent(Swal);

  const requesterLabel = useMemo(() => {
    if (userData?.isAMT) return "AMT";
    if (userData?.isHR) return "HPL";
    return "SSO";
  }, [userData]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    axios
      .get(Utils.apiUrl("brands?limit=99999"), Utils.apiHeaders())
      .then((res) => {
        if (res.data.success) {
          setBrands(res.data.data || []);
        }
      })
      .catch(() => {});

    axios
      .get(Utils.apiUrl("hr/hotel-access/countries"), Utils.apiHeaders())
      .then((res) => {
        if (res.data.success) {
          setCountries(res.data.data || []);
        }
      })
      .catch(() => {});
  }, []);

  const updateFilter = (field) => (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const paginatedResults = results.data.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const paginationInfo = {
    start: results.data.length === 0 ? 0 : (currentPage - 1) * pageSize,
    limit: pageSize,
    total: results.data.length,
  };

  const searchHotels = (overrideFilters = null) => {
    const maybeFilters =
      overrideFilters &&
      typeof overrideFilters === "object" &&
      !("target" in overrideFilters)
        ? overrideFilters
        : null;

    const activeFilters = maybeFilters || filters || {};
    const keyword = String(activeFilters.keyword || "").trim();

    setCurrentPage(1);
    setResults((prev) => ({ ...prev, loading: true, searched: true }));

    const params = new URLSearchParams();
    if (activeFilters.brand_id) params.set("brand_id", activeFilters.brand_id);
    if (activeFilters.country_id)
      params.set("country_id", activeFilters.country_id);
    if (keyword) params.set("keyword", keyword);

    axios
      .get(
        Utils.apiUrl(`hr/hotel-access/search?${params.toString()}`),
        Utils.apiHeaders(),
      )
      .then((res) => {
        if (res.data.success) {
          setResults({
            loading: false,
            searched: true,
            data: res.data.data || [],
          });
        } else {
          setResults({ loading: false, searched: true, data: [] });
          MySwal.fire(
            "Error",
            res.data.message || "Failed to search hotels.",
            "error",
          );
        }
      })
      .catch(() => {
        setResults({ loading: false, searched: true, data: [] });
        MySwal.fire("Error", "Failed to search hotels.", "error");
      });
  };

  const requestAgain = (hotel) => {
    axios
      .post(
        Utils.apiUrl("hr/hotel-access/request"),
        { hotel_id: hotel.id, requestAgain: true },
        Utils.apiHeaders(),
      )
      .then((res) => {
        if (res.data.success) {
          MySwal.fire(
            "Requested",
            "Hotel access request submitted.",
            "success",
          );
          setResults((prev) => ({
            ...prev,
            data: prev.data.map((item) =>
              item.id === hotel.id
                ? { ...item, pending_request: true, rejected_request: false }
                : item,
            ),
          }));
          onRequestSubmitted();
        } else {
          MySwal.fire(
            "Error",
            res.data.message || "Failed to submit request.",
            "error",
          );
        }
      })
      .catch(() => {
        MySwal.fire("Error", "Failed to submit request.", "error");
      });
  };

  const requestAccess = (hotel) => () => {
    MySwal.fire({
      title: (userData.isHR ? "Get" : "Request") + " Hotel Access!",
      text: `${userData.isHR ? "Get" : "Request"} access for ${hotel.name}!`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText:
        userData.isHR && userHasHotel() ? "Get Access" : "Request Access",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "rounded-xl",
        confirmButton:
          "bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded mx-2",
        cancelButton:
          "bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded mx-2",
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios
        .post(
          Utils.apiUrl("hr/hotel-access/request"),
          { hotel_id: hotel.id },
          Utils.apiHeaders(),
        )
        .then((res) => {
          if (res.data.success) {
            MySwal.fire(
              "Requested",
              res.data.message || "Hotel access request submitted.",
              "success",
            );
            setResults((prev) => ({
              ...prev,
              data: prev.data.map((item) =>
                item.id === hotel.id
                  ? {
                      ...item,
                      has_access: res.data.has_access || false,
                      pending_request: res.data.pending_request || false,
                      rejected_request: false,
                    }
                  : item,
              ),
            }));
            onRequestSubmitted();
          } else {
            if (res.data.request && userData?.isHR) {
              MySwal.fire({
                title: "Error",
                text: res.data.message || "Failed to submit request.",
                icon: "error",
                showCancelButton: true,
                confirmButtonText: "Request Access",
                cancelButtonText: "Close",
                allowOutsideClick: false,
                allowEscapeKey: true,
                customClass: {
                  confirmButton:
                    "bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded mx-2",
                  cancelButton:
                    "bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded mx-2",
                },
              }).then((result) => {
                if (result.isConfirmed) {
                  requestAgain(hotel);
                }
              });
            } else {
              MySwal.fire(
                "Error",
                res.data.message || "Failed to submit request.",
                "error",
              );
            }
          }
        })
        .catch(() => {
          MySwal.fire("Error", "Failed to submit request.", "error");
        });
    });
  };

  const userHasHotel = () =>
    results.data.filter((h) => h.has_access).length === 0;

  const renderButton = (hotel) => {
    if (hotel.has_access || hotel.approved_request) {
      return (
        <button
          type="button"
          disabled
          className="rounded bg-gray-200 px-3 py-1.5 text-sm text-gray-500 cursor-not-allowed"
        >
          Access Available
        </button>
      );
    }

    if (hotel.pending_request) {
      return (
        <button
          type="button"
          disabled
          className="rounded bg-yellow-100 px-3 py-1.5 text-sm text-yellow-700 cursor-not-allowed"
        >
          Request Pending
        </button>
      );
    }

    if (hotel.rejected_request) {
      return (
        <>
          <span className="text-xs bg-red-200 text-red-700 p-1 flex items-center rounded mr-2">
            Request Rejected
          </span>
          <button
            type="button"
            onClick={requestAccess(hotel)}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
          >
            {userHasHotel() ? "Access Again" : "Request Access Again"}
          </button>
        </>
      );
    }

    return (
      <button
        type="button"
        onClick={requestAccess(hotel)}
        className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
      >
        {userData.isHR && userHasHotel() ? "Access" : "Request Access"}
      </button>
    );
  };

  const getHotelSummary = (hotel) => {
    const summary = [hotel.marsha, hotel.brand_name, hotel.country_name].filter(
      Boolean,
    );

    return summary.length > 0 ? summary.join(", ") : "-";
  };

  if (!open) {
    return null;
  }

  const handleHotelCreated = (hotel, message) => {
    const nextFilters = {
      brand_id: String(hotel?.brand_id || filters.brand_id || ""),
      country_id: String(hotel?.country_id || filters.country_id || ""),
      keyword: hotel?.marsha || hotel?.name || filters.keyword || "",
    };

    setFilters(nextFilters);
    setShowAddHotelForm(false);
    searchHotels(nextFilters);
    onRequestSubmitted();
    MySwal.fire("Success", message || "Hotel added successfully.", "success");
  };

  return (
    <>
      {!showAddHotelForm && (
        <div
          className={`fixed inset-0 flex items-center justify-center p-4 z-40 bg-black bg-opacity-50`}
          onClick={onClose}
        >
          <section
            className={`relative max-h-[90vh] w-full max-w-6xl rounded-2xl bg-white shadow-2xl ${
              showAddHotelForm ? "pointer-events-none opacity-0" : ""
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <div className="section-title !mb-0 flex items-center justify-between">
                <div id="hotel-access-request-title">
                  <i className="fas fa-key"></i>
                  Hotel Access Request
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close hotel access request dialog"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Select Brand
                  </label>
                  <select
                    value={filters.brand_id}
                    onChange={updateFilter("brand_id")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">All Brands</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Select Country
                  </label>
                  <select
                    value={filters.country_id}
                    onChange={updateFilter("country_id")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">All Countries</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Search by Hotel Name or Marsha Code
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={filters.keyword}
                      onChange={updateFilter("keyword")}
                      placeholder="Enter hotel name or marsha code"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => searchHotels()}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>

              {results.loading && (
                <div className="mt-6 rounded bg-gray-50 p-4 text-center text-gray-500">
                  Searching hotels...
                </div>
              )}

              {!results.loading && results.data.length > 0 && (
                <div className="mt-6 space-y-3">
                  {paginatedResults.map((hotel) => (
                    <div
                      key={hotel.id}
                      className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm md:flex-row md:items-center md:justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-gray-900">
                          {hotel.name}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          {getHotelSummary(hotel)}
                        </p>
                      </div>
                      <div className="flex shrink-0 md:justify-end">
                        {renderButton(hotel)}
                      </div>
                    </div>
                  ))}

                  {results.data.length > pageSize &&
                    Utils.showPagination(paginationInfo, (page) => () => {
                      setCurrentPage(page);
                    })}
                </div>
              )}

              {!results.loading &&
                results.searched &&
                results.data.length === 0 && (
                  <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
                    <p className="text-sm text-gray-600">
                      No matching hotels found.
                    </p>
                    <button
                      type="button"
                      className="mt-4 rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700"
                      onClick={() => setShowAddHotelForm(true)}
                    >
                      Add New Hotel
                    </button>
                  </div>
                )}
            </div>
          </section>
        </div>
      )}
      <AddNewHotelForm
        open={showAddHotelForm}
        onClose={() => setShowAddHotelForm(false)}
        onCreated={handleHotelCreated}
        brands={brands}
        countries={countries}
        defaultValues={{
          name: filters.keyword,
          brand_id: filters.brand_id,
          country_id: filters.country_id,
        }}
      />
    </>
  );
};

export default HotelAccessRequest;
